import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { QueryDeskQueueParams, queryDeskQueue } from 'supabase/desk'

/**
 * One page of one desk tab. Every desk query key starts with 'desk', the
 * prefix the decision mutations invalidate (UI-SPEC §B), so a decision
 * refreshes every tab and badge at once. Tab, page and page size all travel in
 * the key, so a change fetches instead of serving another page's cache.
 *
 * `count` is the server's exact count for this tab; never derive a total from
 * `rows` (PostgREST caps a select at 1000 rows).
 */
export const useQueryDeskQueue = ({ tab, page, perPage }: QueryDeskQueueParams) => {
  const { isLoading, isFetching, isError, error, data } = useQuery({
    queryKey: ['desk', 'queue', { tab, page, perPage }],
    queryFn: () => queryDeskQueue({ tab, page, perPage }),
    placeholderData: keepPreviousData,
    // Re-read with the badges (useQueryDeskCounts), so the list and its
    // count never sit behind them (AD-WR-04). The 'desk' prefix defaults in
    // utils/queryClient.ts also re-read it on focus and remount.
    refetchInterval: 60_000
  })

  return {
    isLoading,
    isFetching,
    isError,
    error,
    rows: data?.rows,
    count: data?.count ?? null,
    outOfRange: data?.outOfRange === true
  }
}
